import { Customer } from '../types';

/** Rechtsformen und Fuellwoerter, die beim Namensvergleich nicht zaehlen. */
const LEGAL_FORMS =
  /\b(gmbh\s*&?\s*co\.?\s*kg|gmbh|ag|kg|ohg|gbr|ug|e\.?\s*k\.?|mbh|co)\b/g;

const UMLAUTS: Record<string, string> = {
  ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss', Ä: 'ae', Ö: 'oe', Ü: 'ue',
};

/**
 * Umlaute in die ae/oe/ue/ss-Schreibweise. MUSS vor dem Zeichenfilter laufen:
 * danach gibt es keine Umlaute mehr, die der Filter zu Leerzeichen machen
 * koennte — und "Müller GmbH" und "Mueller GmbH" normalisieren auf denselben
 * Wert. Beides sind reale Schreibweisen derselben Firma in Steuerberater-
 * Listen, vorher galten sie als verschiedene Kunden.
 */
const replaceUmlauts = (s: string): string => s.replace(/[äöüßÄÖÜ]/g, (ch) => UMLAUTS[ch] ?? ch);

export const normalizeCompanyName = (name: string): string =>
  replaceUmlauts((name ?? '').toLowerCase())
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(LEGAL_FORMS, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Token-Ueberlappung zweier normalisierter Namen (0..1). */
const tokenSimilarity = (a: string, b: string): number => {
  const ta = new Set(a.split(' ').filter(Boolean));
  const tb = new Set(b.split(' ').filter(Boolean));
  if (!ta.size || !tb.size) return 0;
  let common = 0;
  for (const t of ta) if (tb.has(t)) common++;
  return common / Math.max(ta.size, tb.size);
};

/** Bigramme des normalisierten Namens; Leerzeichen fallen weg. */
const bigrams = (s: string): string[] => {
  const t = s.replace(/\s+/g, '');
  const out: string[] = [];
  for (let i = 0; i + 1 < t.length; i += 1) out.push(t.slice(i, i + 2));
  return out;
};

/** Dice-Koeffizient ueber Bigramme (0..1), Vielfachheiten zaehlen mit. */
const bigramSimilarity = (a: string, b: string): number => {
  const ba = bigrams(a);
  const bb = bigrams(b);
  if (!ba.length || !bb.length) return 0;
  const pool = new Map<string, number>();
  for (const g of ba) pool.set(g, (pool.get(g) ?? 0) + 1);
  let common = 0;
  for (const g of bb) {
    const left = pool.get(g) ?? 0;
    if (left > 0) {
      pool.set(g, left - 1);
      common += 1;
    }
  }
  return (2 * common) / (ba.length + bb.length);
};

/**
 * Ab hier zaehlt ein Bigramm-Treffer. Bewusst hoch: die Zeichenaehnlichkeit
 * soll Tippfehler und Schreibvarianten fangen ("Müler" / "Müller"), nicht
 * zwei Firmen, die zufaellig aehnliche Buchstabenfolgen teilen.
 */
const BIGRAM_MIN = 0.85;

/**
 * Aehnlichkeit zweier BEREITS normalisierter Namen: Token-Ueberlappung, und
 * wo die versagt (ein Buchstabe daneben = kein gemeinsames Token) die
 * zeichenbasierte Bigramm-Aehnlichkeit ab BIGRAM_MIN.
 */
const normalizedNameSimilarity = (a: string, b: string): number => {
  const token = tokenSimilarity(a, b);
  if (token >= 0.99) return token;
  const bigram = bigramSimilarity(a, b);
  return bigram >= BIGRAM_MIN ? Math.max(token, bigram) : token;
};

/** Aehnlichkeit zweier ROHER Firmennamen (0..1) — normalisiert selbst. */
export const nameSimilarity = (a: string, b: string): number =>
  normalizedNameSimilarity(normalizeCompanyName(a || ''), normalizeCompanyName(b || ''));

export interface CustomerCandidate {
  name: string;
  email?: string;
  zip?: string;
  city?: string;
  street?: string;
}

export interface DuplicateHit {
  customer: Customer;
  score: number;
  reasons: string[];
}

/**
 * Aehnliche Bestandskunden zu einem Kandidaten. Pure Funktion — die Aufrufer
 * (Neuanlage, Import) laden die Kundenliste selbst. Archivierte werden
 * MITGEPRUEFT: sonst legte man den Kunden neu an, den man gestern archiviert
 * hat, und haette zwei Nummern fuer dieselbe Firma.
 */
export const findSimilarCustomers = (
  candidate: CustomerCandidate,
  existing: Customer[],
): DuplicateHit[] => {
  const candName = normalizeCompanyName(candidate.name || '');
  const candMail = candidate.email?.trim().toLowerCase();
  const hits: DuplicateHit[] = [];
  for (const c of existing) {
    const reasons: string[] = [];
    let score = 0;
    if (candMail && c.email && c.email.trim().toLowerCase() === candMail) {
      score = 1;
      reasons.push('identische E-Mail-Adresse');
    }
    const sim = normalizedNameSimilarity(candName, normalizeCompanyName(c.name));
    const samePlace = Boolean(
      candidate.zip && c.zip && candidate.zip === c.zip &&
      candidate.city && c.city &&
      candidate.city.trim().toLowerCase() === c.city.trim().toLowerCase(),
    );
    if (sim >= 0.99) {
      score = 1;
      reasons.push('gleicher Name');
    } else if (sim >= 0.5) {
      score = Math.max(score, sim * 0.8);
      reasons.push('ähnlicher Name');
      if (samePlace) {
        score = Math.max(score, 0.6);
        reasons.push('gleiche PLZ und Ort');
      }
    } else if (sim > 0 && samePlace) {
      // Schon EIN gemeinsames Namens-Token am selben Ort ist verdaechtig:
      // "Musterbau Hoch- und Tiefbau" neben "Musterbau GmbH" in 20095.
      score = Math.max(score, 0.6);
      reasons.push('ähnlicher Name', 'gleiche PLZ und Ort');
    }
    if (score >= 0.5) {
      if (c.archivedAt) reasons.push('Kunde ist archiviert');
      hits.push({ customer: c, score, reasons });
    }
  }
  return hits.sort((a, b) => b.score - a.score);
};
